/**
 * 従業員タブの時給まわりについて「**何を出すか**」だけを決める（STAFF-WAGE-UX）。
 *
 * ★このファイルは **DOM を触らない**（`wage-alert-view.ts` と同じ作り）。
 *   判断をここだけに置けば、`main.ts` の描画側に「出す／出さない」の分岐が残らない。
 * ★氏名も時給も、ここでは**画面に渡された値を組み立てるだけ**。新しい取得経路は持たない（絶対ルール1）。
 */
import type { Staff, WageHistory } from "./api.js";

/**
 * 社員（`FULL_TIME`）の行に出す文言。
 *
 * ★社員の人件費は時給を使わず `[P]salary_monthly_total ÷ 営業日数`（docs/02 §4.2）。
 *   名前は画面にある実物のまま：設定タブの「社員 月額人件費」（`migrations/0020`）。
 *   ★文言に金額・率の数字は書かない。
 */
export const FULL_TIME_WAGE_NOTE = "月給制（社員の月額は「設定」タブの「社員 月額人件費」）";

/** AC-5。★時刻・率の数字を書かない（パラメータで変わる。書くと変えた日に嘘になる） */
export const NIGHT_WAGE_NOTE = "深夜時間帯の時給は、基本時給と深夜割増率から自動で計算します（入力不要）";

/** 在籍中と退職済みに分ける。★元の並びを保つ */
export function partitionStaff(staff: Staff[]): { active: Staff[]; retired: Staff[] } {
  return {
    active: staff.filter((s) => s.isActive),
    retired: staff.filter((s) => !s.isActive),
  };
}

type EffectiveWage = { staffId: number | string; baseHourlyWage?: number };

/** 画面の日付時点で在籍中のアルバイト（入社日が日付より後の人は、まだ勤務に入らないので含めない） */
const isPartTimerOn = (s: Staff, date: string): boolean =>
  s.isActive && s.employmentType === "PART_TIME" && (s.hiredOn === null || s.hiredOn <= date);

/**
 * 画面の日付時点で時給が無い、在籍中のアルバイト。
 *
 * ★時給の無いアルバイトに勤務が入ると、その日のアルバイト人件費が null になる（`src/infra/staff.ts`）。
 * ★社員は数えない（時給を使わない）。退職者も数えない。
 * ★入社日が日付より後の人も数えない。サーバの `findEffectiveWages` が `hired_on <= 日付` で絞るので、
 *   登録しても「その日の時給」には現れず、数えると**直せない未登録**が残り続ける。
 */
export function unregisteredPartTimers(staff: Staff[], wages: EffectiveWage[], date: string): Staff[] {
  const registered = new Set(wages.map((w) => String(w.staffId)));
  return staff.filter((s) => isPartTimerOn(s, date) && !registered.has(s.id));
}

/**
 * 一覧の上の1行。★未登録が0名でも黙らない（「全員登録済み」と言う）。
 * ★ただし**対象のアルバイトが1人もいない**ときは何も出さない。
 *   「全員」と言うと、誰もいないのに済んだように読める（社員だけの店・従業員0件は別の案内に任せる）。
 */
export function unregisteredHeading(staff: Staff[], count: number, date: string): string | null {
  if (count > 0) return `時給が未登録のアルバイト ${count}名`;
  return staff.some((s) => isPartTimerOn(s, date)) ? "在籍中のアルバイト全員の時給が登録済みです" : null;
}

/** 行のボタン。★登録済みかどうかで文言を分ける */
export function wageButtonLabel(registered: boolean): string {
  return registered ? "時給を変える" : "時給を登録する";
}

/** `<details>` の見出し。0件のときは呼び出し側が出さない */
export const wageHistoryHeading = (count: number): string => `時給の履歴（${count}件）`;
export const retiredHeading = (count: number): string => `退職した従業員（${count}名）`;

/** 実在する `YYYY-MM-DD` か（`Date` は 2026-02-31 を 03-03 に丸めるので往復で確かめる） */
function isBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

/** `reviseStaffWage` の `previousDay` と同じ（UTC 暦日。月初またぎは Date が面倒を見る） */
function previousDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

const yen = (n: number): string => `${n.toLocaleString()}円`;

/**
 * 保存前の「前後」。★**`reviseStaffWage`（src/infra/parameters.ts）の閉じ方と一致させる**。
 *
 * - 旧 ＝ `valid_from < 開始日` かつ（`valid_to` が無い or `>= 開始日`）の1行。旧は開始日の前日で閉じる
 * - 次 ＝ 開始日より後で最も早い `valid_from`。あれば新を**その前日で閉じる**
 * - ★開始日ちょうどの行が既にあると、旧は閉じられず期間が重なって保存できない。
 *   そのときは前後を出さず、そのことを言う
 *
 * @param history その従業員の履歴（`GET /api/wages` は全期間を返す。並びは問わない）
 * @param rawWage 入力欄の文字列。空・数値でない・負は `null`（まだ出さない）
 */
export function previewWageChange(
  history: WageHistory[],
  validFrom: string,
  rawWage: string,
): string | null {
  const text = rawWage.trim();
  if (text === "" || !isBusinessDate(validFrom)) return null;
  const wage = Number(text);
  if (!Number.isFinite(wage) || wage < 0) return null;

  const same = history.find((h) => h.validFrom === validFrom);
  if (same !== undefined) {
    return `${validFrom}からの時給は登録済みです。別の適用開始日にしてください`;
  }

  const old = history.find((h) => h.validFrom < validFrom && (h.validTo === null || h.validTo >= validFrom));
  const oldText = old?.baseHourlyWage === undefined ? "（未登録）" : yen(old.baseHourlyWage);
  const closing = `〜${previousDay(validFrom)} ${oldText}`;

  const next = history
    .filter((h) => h.validFrom > validFrom)
    .sort((a, b) => (a.validFrom < b.validFrom ? -1 : 1))[0];
  if (next === undefined) return `${closing} → ${validFrom}から ${yen(wage)}`;

  const nextText = next.baseHourlyWage === undefined ? "（金額は非表示）" : yen(next.baseHourlyWage);
  return (
    `${closing} → ${validFrom}〜${previousDay(next.validFrom)} ${yen(wage)}` +
    `（${next.validFrom}からは登録済みの ${nextText}）`
  );
}
